import { useTranslation } from "react-i18next";
import { Activity, Zap, Target, TrendingUp, Shield, Clock } from "lucide-react";
import { LanguageSwitcher } from "./LanguageSwitcher";

export function Header() {
  const { t } = useTranslation();

  const FEATURES = [
    {
      icon: Target,
      title: t("header.features.extremeCapture.title"),
      description: t("header.features.extremeCapture.description"),
      color: "text-blue-600",
      bgColor: "bg-blue-50",
    },
    {
      icon: TrendingUp,
      title: t("header.features.dailyAnalysis.title"),
      description: t("header.features.dailyAnalysis.description"),
      color: "text-green-600",
      bgColor: "bg-green-50",
    },
    {
      icon: Shield,
      title: t("header.features.smartScoring.title"),
      description: t("header.features.smartScoring.description"),
      color: "text-purple-600",
      bgColor: "bg-purple-50",
    },
    {
      icon: Clock,
      title: t("header.features.realTimeMonitor.title"),
      description: t("header.features.realTimeMonitor.description"),
      color: "text-orange-600",
      bgColor: "bg-orange-50",
    },
  ];

  return (
    <header className="sticky top-0 z-30 bg-white/95 backdrop-blur-md border-b border-gray-100 shadow-sm">
      {/* Top bar */}
      <div className="flex items-center justify-between max-w-screen-2xl mx-auto px-6 py-3">
        {/* Logo & Title */}
        <div className="flex items-center gap-4">
          <div className="relative">
            <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-blue-600 via-blue-500 to-cyan-500 flex items-center justify-center shadow-lg shadow-blue-500/25">
              <Activity className="w-5 h-5 text-white" />
            </div>
            <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-green-500 rounded-full border-2 border-white flex items-center justify-center">
              <Zap className="w-2.5 h-2.5 text-white" />
            </div>
          </div>
          <div>
            <h1 className="text-lg font-bold text-gray-900 tracking-tight">
              KbDeviationFrom
            </h1>
            <p className="text-xs text-gray-500">{t("header.subtitle")}</p>
          </div>
        </div>

        {/* Status */}
        <div className="flex items-center gap-3">
          {/* Connection status */}
          <div className="flex items-center gap-2 px-3 py-1.5 bg-gradient-to-r from-gray-50 to-gray-100 rounded-lg border border-gray-200">
            <div className="relative">
              <div className="w-2 h-2 rounded-full bg-green-500" />
              <div className="absolute inset-0 w-2 h-2 rounded-full bg-green-500 animate-ping" />
            </div>
            <span className="text-xs font-medium text-gray-600">
              {t("header.liveData")}
            </span>
          </div>

          {/* Language Switcher */}
          <LanguageSwitcher />
        </div>
      </div>

      {/* Features bar */}
      <div className="bg-gradient-to-r from-slate-50 via-blue-50/30 to-slate-50 border-t border-gray-100">
        <div className="max-w-screen-2xl mx-auto px-6 py-2.5">
          <div className="flex items-center justify-center gap-8">
            {FEATURES.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <div
                  key={index}
                  className="flex items-center gap-2.5 group cursor-default"
                >
                  <div
                    className={`w-7 h-7 rounded-lg ${feature.bgColor} flex items-center justify-center group-hover:scale-110 transition-transform`}
                  >
                    <Icon className={`w-3.5 h-3.5 ${feature.color}`} />
                  </div>
                  <div>
                    <div className="text-xs font-semibold text-gray-800">
                      {feature.title}
                    </div>
                    <div className="text-[10px] text-gray-500 hidden lg:block">
                      {feature.description}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Algorithm highlight */}
      <div className="bg-gradient-to-r from-blue-600 via-blue-500 to-cyan-500 text-white">
        <div className="max-w-screen-2xl mx-auto px-6 py-2.5">
          <div className="flex items-center justify-center gap-6 text-xs">
            <div className="flex items-center gap-2">
              <span className="font-bold bg-white/20 px-2 py-0.5 rounded">
                {t("header.coreAlgorithm")}
              </span>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 bg-green-400 rounded-full"></span>
              <span>{t("header.algorithms.rsi")}</span>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 bg-yellow-400 rounded-full"></span>
              <span>{t("header.algorithms.macd")}</span>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 bg-red-400 rounded-full"></span>
              <span>{t("header.algorithms.multiPeriod")}</span>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 bg-purple-400 rounded-full"></span>
              <span>{t("header.algorithms.scoring")}</span>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
}
